import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useLot } from '../contexts/LotContext';
import { useMultiLot } from '../contexts/MultiLotContext';
import Modal from '../components/Modal';
import PrintTicketModal from '../components/PrintTicketModal';
import Menu, { type MenuItem } from '../components/Menu';
import Pagination from '../components/Pagination';
import VehicleDetailPanel, { VehicleDetailContext } from '../components/VehicleDetailPanel';
import { usePagination } from '../hooks/usePagination';
import {
    PaginationParams,
    PageResponse,
    ViolationDetail,
    ViolationTrendPoint,
    ViolatorRow,
} from '../types';
import { violationService, lotPricingService, registryService } from '../utils/api';
import './Violators.css';

const VIOLATION_TYPE_OPTIONS = [
    { value: 'OVERSTAY', label: 'Overstay' },
    { value: 'NO_PERMIT', label: 'No Permit' },
    { value: 'UNPAID_SESSION', label: 'Unpaid' },
];

type SortableKey =
    | 'plateNumber'
    | 'openViolations'
    | 'status'
    | 'totalAmountOwed'
    | 'latestViolationTimestamp'
    | 'latestLotName';

const Violators: React.FC = () => {
    const { lotId } = useParams<{ lotId: string }>();
    const { lotData } = useLot();
    const { isMultiLotMode, getSelectedLotIds, selectedLots } = useMultiLot();

    const selectedLotIds = useMemo(
        () => (isMultiLotMode ? getSelectedLotIds() : lotId ? [lotId] : []),
        [isMultiLotMode, getSelectedLotIds, lotId]
    );
    const lotIdsKey = selectedLotIds.join(',');

    // Responsive: hide side panel on small screens.
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const update = () => setIsMobile(window.innerWidth <= 768);
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    // Page-level data via usePagination wired to the consolidated endpoint.
    const fetchViolators = useCallback(
        async (params: PaginationParams): Promise<PageResponse<ViolatorRow>> => {
            const response = await violationService.listViolators({
                lotIds: selectedLotIds,
                page: params.page,
                size: params.size,
                sortBy: params.sortBy,
                sortDirection: params.sortDirection,
                search: params.search,
            });
            return response as PageResponse<ViolatorRow>;
        },
        [lotIdsKey]
    );

    const {
        items,
        totalElements,
        totalPages,
        page,
        sortBy,
        sortDirection,
        loading,
        error,
        goToPage,
        setSort,
        setSearch,
        refresh,
    } = usePagination<ViolatorRow>({
        fetchFn: fetchViolators,
        initialPage: 0,
        initialSize: 10,
        initialSortBy: 'latestViolationTimestamp',
        initialSortDirection: 'desc',
        enabled: selectedLotIds.length > 0,
        resetKey: lotIdsKey,
    });

    // Search input (uncontrolled-ish; debounce is inside usePagination).
    const [searchQuery, setSearchQuery] = useState('');
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.toUpperCase();
        if (value.length > 10) return;
        setSearchQuery(value);
        setSearch(value);
    };

    // Expand state + lazy-loaded details.
    const [expandedPlates, setExpandedPlates] = useState<Set<string>>(new Set());
    const [violationDetails, setViolationDetails] = useState<Record<string, ViolationDetail[]>>({});
    const fetchViolationDetails = useCallback(
        async (row: ViolatorRow) => {
            if (!row.latestLotId || violationDetails[row.plateNumber]) return;
            try {
                const response = await violationService.getOpenViolationsByPlate(row.latestLotId, row.plateNumber);
                setViolationDetails((prev) => ({ ...prev, [row.plateNumber]: response.data || [] }));
            } catch (err) {
                console.error('Failed to fetch violation details', err);
            }
        },
        [violationDetails]
    );

    const toggleExpanded = (row: ViolatorRow) => {
        setExpandedPlates((prev) => {
            const next = new Set(prev);
            if (next.has(row.plateNumber)) {
                next.delete(row.plateNumber);
            } else {
                next.add(row.plateNumber);
                void fetchViolationDetails(row);
            }
            return next;
        });
    };

    // Auto-expand when search returns exactly one plate.
    useEffect(() => {
        if (searchQuery && items.length === 1) {
            const row = items[0];
            if (!expandedPlates.has(row.plateNumber)) {
                setExpandedPlates(new Set([row.plateNumber]));
                void fetchViolationDetails(row);
            }
        }
    }, [searchQuery, items, fetchViolationDetails, expandedPlates]);

    // Side-panel context: hover overrides while the user is hovering; pin is the fallback when nothing is hovered.
    const [hoveredCtx, setHoveredCtx] = useState<VehicleDetailContext | null>(null);
    const [pinnedCtx, setPinnedCtx] = useState<VehicleDetailContext | null>(null);
    // Tracks the most recently hovered row so we can pin it when the cursor enters the side panel
    // (treating the row→panel motion as a click).
    const lastHoveredRef = useRef<VehicleDetailContext | null>(null);
    const activeCtx = hoveredCtx || pinnedCtx;
    const handlePanelMouseEnter = () => {
        if (lastHoveredRef.current) {
            setPinnedCtx({ ...lastHoveredRef.current, isPinned: true });
        }
    };

    // Trend data for the active context, cached by plate.
    const [trendCache, setTrendCache] = useState<Record<string, ViolationTrendPoint[]>>({});
    const [trendLoading, setTrendLoading] = useState(false);
    useEffect(() => {
        const plate = activeCtx?.plateNumber;
        if (!plate || trendCache[plate]) return;
        let cancelled = false;
        setTrendLoading(true);
        violationService
            .getViolatorTrend(plate, selectedLotIds, 7)
            .then((data) => {
                if (!cancelled) setTrendCache((prev) => ({ ...prev, [plate]: data }));
            })
            .catch((err) => console.error('Failed to fetch violation trend', err))
            .finally(() => {
                if (!cancelled) setTrendLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [activeCtx?.plateNumber, lotIdsKey]);

    const buildCtxFromRow = (row: ViolatorRow, pinned = false): VehicleDetailContext => ({
        plateNumber: row.plateNumber,
        violationId: row.latestViolationId,
        vehicleMake: row.vehicleMake,
        vehicleModel: row.vehicleModel,
        vehicleColor: row.vehicleColor,
        evidenceURL: row.evidenceURL,
        plateConfidence: row.plateConfidence,
        vehicleConfidence: row.vehicleConfidence,
        lastSeenLocation: row.lastSeenLocation,
        timestamp: row.latestViolationTimestamp,
        notes: violationDetails[row.plateNumber]?.[0]?.initialNotes || '',
        canValidate: row.allowValidation && row.status === 'ACTIVE',
        isPinned: pinned,
    });

    const buildCtxFromDetail = (
        row: ViolatorRow,
        detail: ViolationDetail,
        pinned = false
    ): VehicleDetailContext => ({
        plateNumber: row.plateNumber,
        violationId: detail.violationTrackerId,
        vehicleMake: detail.vehicleMake || row.vehicleMake,
        vehicleModel: detail.vehicleModel || row.vehicleModel,
        vehicleColor: detail.vehicleColor || row.vehicleColor,
        evidenceURL: detail.evidenceURL || row.evidenceURL,
        plateConfidence: row.plateConfidence,
        vehicleConfidence: row.vehicleConfidence,
        lastSeenLocation: row.lastSeenLocation,
        timestamp: detail.violationTimestamp,
        notes: detail.initialNotes || '',
        canValidate: row.allowValidation && row.status === 'ACTIVE',
        isPinned: pinned,
    });

    // Clicking the row expands / collapses (and pins the side panel to that plate while expanded).
    const handleRowClick = (row: ViolatorRow) => {
        const isExpanded = expandedPlates.has(row.plateNumber);
        if (isExpanded) {
            setExpandedPlates((prev) => {
                const next = new Set(prev);
                next.delete(row.plateNumber);
                return next;
            });
            if (pinnedCtx?.plateNumber === row.plateNumber) setPinnedCtx(null);
        } else {
            setExpandedPlates((prev) => {
                const next = new Set(prev);
                next.add(row.plateNumber);
                return next;
            });
            void fetchViolationDetails(row);
            setPinnedCtx(buildCtxFromRow(row, true));
        }
    };

    // Quick-scrub state: which sub-violation index is currently focused per plate.
    const [scrubIndex, setScrubIndex] = useState<Record<string, number>>({});
    const setScrub = (plate: string, idx: number) => setScrubIndex((prev) => ({ ...prev, [plate]: idx }));

    // ---- Sorting helpers ----
    const handleSort = (column: SortableKey) => {
        if (sortBy === column) {
            setSort(column, sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSort(column, 'desc');
        }
    };

    const renderSortHeader = (label: string, column: SortableKey, extraClass = '') => (
        <th
            onClick={() => handleSort(column)}
            className={`sortable-column ${sortBy === column ? 'active' : ''} ${extraClass}`}
        >
            <span>{label}</span>
            <img
                src={`/assets/tables/${sortBy === column ? 'SortArrowSelected.svg' : 'SortArrow.svg'}`}
                alt="Sort"
                className={`sort-arrow ${sortDirection}`}
            />
        </th>
    );

    // ---- Formatting ----
    const formatCurrency = (amount: number | null) =>
        amount === null || amount === undefined ? '$0.00' : `$${Number(amount).toFixed(2)}`;
    const formatDate = (d: string) =>
        new Date(d).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    const formatTime = (d: string) =>
        new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    const formatRelative = (d: string) => {
        const diffMs = Date.now() - new Date(d).getTime();
        if (diffMs < 0) return formatTime(d);
        const min = Math.floor(diffMs / 60000);
        if (min < 1) return 'just now';
        if (min < 60) return `${min} ${min === 1 ? 'minute' : 'minutes'} ago`;
        const hr = Math.floor(min / 60);
        if (hr < 24) return `${hr} ${hr === 1 ? 'hour' : 'hours'} ago`;
        return null;
    };
    // Date column: shows only the date by default; if the violation is within the last 24h, show relative time instead.
    const renderDateCell = (d: string) => {
        const rel = formatRelative(d);
        return <span className="violators-date-single">{rel || formatDate(d)}</span>;
    };
    const renderDateStack = (d: string) => {
        const rel = formatRelative(d);
        if (rel) {
            return (
                <div className="violators-date-stack">
                    <span className="violators-date-primary">{rel}</span>
                    <span className="violators-date-secondary">{formatDate(d)}</span>
                </div>
            );
        }
        return (
            <div className="violators-date-stack">
                <span className="violators-date-primary">{formatDate(d)}</span>
                <span className="violators-date-secondary">{formatTime(d)}</span>
            </div>
        );
    };

    const getViolationTypeDisplay = (type: string) => {
        switch (type) {
            case 'OVERSTAY':
                return 'Overstay';
            case 'UNPAID_SESSION':
                return 'Unpaid';
            case 'NO_PERMIT':
                return 'No Permit';
            default:
                return type;
        }
    };

    // ---- Modal state (add violation, clear, print) ----
    const [isAddOpen, setAddOpen] = useState(false);
    const [isLiveHitOpen, setLiveHitOpen] = useState(false);
    const [addData, setAddData] = useState({
        plateNumber: '',
        vehicleMake: '',
        vehicleModel: '',
        vehicleColor: '',
        violationType: '',
        totalViolationAmount: '',
        initialNotes: '',
        selectedLotId: '',
    });
    const [violationMenuOpen, setViolationMenuOpen] = useState(false);
    const [lotMenuOpen, setLotMenuOpen] = useState(false);
    const [lotPricing, setLotPricing] = useState<any>(null);

    useEffect(() => {
        if (!lotId) return;
        lotPricingService
            .getLatestPricingByLotId(lotId)
            .then(setLotPricing)
            .catch((err) => console.warn('Failed to fetch lot pricing', err));
    }, [lotId]);

    const openAdd = (plate?: string, targetLotId?: string) => {
        setAddData({
            plateNumber: plate || searchQuery,
            vehicleMake: '',
            vehicleModel: '',
            vehicleColor: '',
            violationType: '',
            totalViolationAmount: lotPricing?.ticketAmount ? Number(lotPricing.ticketAmount).toFixed(2) : '',
            initialNotes: '',
            selectedLotId: targetLotId || (isMultiLotMode ? selectedLotIds[0] : lotId || ''),
        });
        setAddOpen(true);
    };

    const violationTypeLabel = useMemo(() => {
        const m = VIOLATION_TYPE_OPTIONS.find((o) => o.value === addData.violationType);
        return m ? m.label : 'Violation Type';
    }, [addData.violationType]);

    const violationMenuItems: MenuItem[] = useMemo(
        () =>
            VIOLATION_TYPE_OPTIONS.map((opt) => ({
                key: opt.value,
                label: opt.label,
                selected: addData.violationType === opt.value,
                onSelect: () => setAddData((p) => ({ ...p, violationType: opt.value })),
            })),
        [addData.violationType]
    );

    const lotMenuItems: MenuItem[] = useMemo(() => {
        if (!isMultiLotMode) return [];
        return selectedLots.map((l) => ({
            key: l.lotId,
            label: l.lotName,
            selected: addData.selectedLotId === l.lotId,
            onSelect: () => setAddData((p) => ({ ...p, selectedLotId: l.lotId })),
        }));
    }, [isMultiLotMode, selectedLots, addData.selectedLotId]);

    const selectedLotLabel = useMemo(() => {
        if (!isMultiLotMode) return '';
        const l = selectedLots.find((x) => x.lotId === addData.selectedLotId);
        return l ? l.lotName : 'Select Lot';
    }, [isMultiLotMode, selectedLots, addData.selectedLotId]);

    const submitAddViolation = async () => {
        const lotForRecord = addData.selectedLotId || lotId;
        if (!lotForRecord || !addData.plateNumber.trim() || !addData.violationType) return;
        try {
            await violationService.recordViolation({
                plateNumber: addData.plateNumber.trim(),
                lotId: lotForRecord,
                violationType: addData.violationType,
                violationTimestamp: new Date().toISOString(),
                vehicleMake: addData.vehicleMake.trim() || 'Unknown',
                vehicleModel: addData.vehicleModel.trim() || 'Unknown',
                vehicleColor: addData.vehicleColor.trim() || 'Unknown',
                totalViolationAmount: addData.totalViolationAmount || '0',
                initialNotes: addData.initialNotes.trim(),
            });
            setAddOpen(false);
            refresh();
        } catch (err: any) {
            alert(`Failed to create violation: ${err.response?.data?.message || err.message || 'Unknown error'}`);
        }
    };

    // Clear modal
    const [clearTarget, setClearTarget] = useState<
        | { kind: 'single'; violationId: string; plateNumber: string; lotId: string }
        | { kind: 'all'; plateNumber: string; lotId: string }
        | null
    >(null);
    const confirmClear = async () => {
        if (!clearTarget) return;
        try {
            const updatedBy = 'operator';
            if (clearTarget.kind === 'all') {
                await violationService.updateAllViolationsByPlate(
                    clearTarget.plateNumber,
                    clearTarget.lotId,
                    updatedBy,
                    'DISMISSED'
                );
            } else {
                await violationService.updateSingleViolation(clearTarget.violationId, updatedBy, 'DISMISSED');
            }
            setClearTarget(null);
            // Clear cached details so the next expand refetches.
            setViolationDetails((prev) => {
                const next = { ...prev };
                delete next[clearTarget.plateNumber];
                return next;
            });
            refresh();
        } catch (err: any) {
            alert(`Failed to clear violation: ${err.response?.data?.message || err.message || 'Unknown error'}`);
        }
    };

    // Print modal
    const [printState, setPrintState] = useState<{ details: ViolationDetail[]; isSummary: boolean } | null>(null);
    const handlePrintSummary = async (row: ViolatorRow) => {
        let details = violationDetails[row.plateNumber];
        if (!details && row.latestLotId) {
            const response = await violationService.getOpenViolationsByPlate(row.latestLotId, row.plateNumber);
            details = response.data || [];
            setViolationDetails((prev) => ({ ...prev, [row.plateNumber]: details! }));
        }
        if (details && details.length > 0) {
            setPrintState({ details, isSummary: true });
        }
    };
    const handlePrintIndividual = (detail: ViolationDetail) => {
        setPrintState({ details: [detail], isSummary: false });
    };

    // Validate (placeholder — hooks into existing vehicle validation system if/when wired)
    const handleValidate = (row: ViolatorRow) => {
        if (!row.allowValidation || row.status !== 'ACTIVE') return;
        // TODO: wire to the existing vehicle validation modal / flow.
        alert(`Validation flow for ${row.plateNumber} — opens the existing validate session UI.`);
    };

    // Add to Registry — one-click add with prefilled vehicle data.
    // Tracks which plates have been added so the button flips to "Added ✓" with no toast.
    const [registryAdded, setRegistryAdded] = useState<Set<string>>(new Set());
    const handleAddToRegistry = async (row: ViolatorRow) => {
        const targetLotId = row.latestLotId || lotId;
        if (!targetLotId || registryAdded.has(row.plateNumber)) return;
        try {
            await registryService.createRegistryEntry({
                lotId: targetLotId,
                plateNumber: row.plateNumber,
                name: [row.vehicleColor, row.vehicleMake, row.vehicleModel].filter(Boolean).join(' ') || row.plateNumber,
                createdBy: 'operator',
            });
            setRegistryAdded((prev) => {
                const next = new Set(prev);
                next.add(row.plateNumber);
                return next;
            });
        } catch (err) {
            console.error('Failed to add to registry', err);
        }
    };

    // Helper to render menu icons (sized + tinted via the .violators-row-menu CSS).
    const menuIcon = (src: string) => <img src={src} alt="" aria-hidden="true" className="row-menu-icon" />;

    // Per-row "..." action menu. Order: positive actions first, destructive Clear All at the bottom.
    const buildRowActions = (row: ViolatorRow): MenuItem[] => [
        {
            key: 'print',
            label: 'Print',
            icon: menuIcon('/assets/violations/print.svg'),
            onSelect: () => handlePrintSummary(row),
        },
        {
            key: 'validate',
            label: 'Validate',
            icon: menuIcon('/assets/violations/validate.svg'),
            disabled: !row.allowValidation || row.status !== 'ACTIVE',
            onSelect: () => handleValidate(row),
        },
        {
            key: 'registry',
            label: registryAdded.has(row.plateNumber) ? '✓ Added to registry' : 'Add to registry',
            icon: menuIcon('/assets/violations/add_to_registry.svg'),
            disabled: registryAdded.has(row.plateNumber),
            onSelect: () => handleAddToRegistry(row),
        },
        {
            key: 'add',
            label: 'Add violation',
            icon: menuIcon('/assets/violations/add_violation.svg'),
            onSelect: () => openAdd(row.plateNumber, row.latestLotId || undefined),
        },
        {
            key: 'clear',
            label: 'Clear all',
            icon: menuIcon('/assets/violations/clear.svg'),
            destructive: true,
            tone: 'danger',
            onSelect: () => setClearTarget({ kind: 'all', plateNumber: row.plateNumber, lotId: row.latestLotId || lotId || '' }),
        },
    ];

    const buildDetailActions = (row: ViolatorRow, detail: ViolationDetail): MenuItem[] => [
        {
            key: 'print',
            label: 'Print',
            icon: menuIcon('/assets/violations/print.svg'),
            onSelect: () => handlePrintIndividual(detail),
        },
        {
            key: 'clear',
            label: 'Clear',
            icon: menuIcon('/assets/violations/clear.svg'),
            destructive: true,
            tone: 'danger',
            onSelect: () =>
                setClearTarget({
                    kind: 'single',
                    violationId: detail.violationTrackerId,
                    plateNumber: row.plateNumber,
                    lotId: row.latestLotId || lotId || '',
                }),
        },
    ];

    // Quick-scrub controls — sync side-panel + scroll into view.
    const detailRowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
    const scrubTo = (row: ViolatorRow, nextIndex: number) => {
        const details = violationDetails[row.plateNumber] || [];
        if (details.length === 0) return;
        const clamped = (nextIndex + details.length) % details.length;
        setScrub(row.plateNumber, clamped);
        const detail = details[clamped];
        setPinnedCtx(buildCtxFromDetail(row, detail, true));
        const key = `${row.plateNumber}:${detail.violationTrackerId}`;
        detailRowRefs.current.get(key)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    };

    // ---- Notes save through to backend ----
    const handleSaveNotes = async (notes: string) => {
        const ctx = activeCtx;
        if (!ctx?.violationId) return;
        await violationService.updateViolationNotes(ctx.violationId, notes);
        // Propagate locally so the UI updates without a full refresh.
        setViolationDetails((prev) => {
            const next = { ...prev };
            for (const plate of Object.keys(next)) {
                next[plate] = next[plate].map((d) =>
                    d.violationTrackerId === ctx.violationId ? { ...d, initialNotes: notes } : d
                );
            }
            return next;
        });
        if (pinnedCtx?.violationId === ctx.violationId) setPinnedCtx({ ...pinnedCtx, notes });
        if (hoveredCtx?.violationId === ctx.violationId) setHoveredCtx({ ...hoveredCtx, notes });
    };

    const showLotColumn = isMultiLotMode && !isMobile;

    return (
        <div className="violators-page-wrapper">
            <div className="sticky-header">
                <div className="top-row">
                    <h1>Manage Violators</h1>
                </div>
                <p>
                    All violators who are actively flagged in{' '}
                    {isMultiLotMode ? 'your selected lots' : lotData?.lotName || 'this lot'}.
                </p>
                <div className="violators-toolbar">
                    <div className="search-bar violators-search">
                        <img src="/assets/SearchBarIcon.svg" alt="Search" />
                        <input
                            type="text"
                            placeholder="Search Plate..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            maxLength={10}
                            className="violators-search-input"
                        />
                    </div>
                    <div className="violators-toolbar-actions">
                        <button
                            className="violators-action-button button-secondary"
                            onClick={() => setLiveHitOpen(true)}
                        >
                            <img src="/assets/violations/radio.svg" alt="" />
                            <span>Live HIT</span>
                        </button>
                        <button className="violators-action-button button-primary" onClick={() => openAdd()}>
                            <img src="/assets/violations/add_violation.svg" alt="" />
                            <span>Add Violation</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="violators-body">
                <div className="violators-main">
                    <div className="table-container">
                    <table className="violators-table app-table app-table-secondary-hover">
                        <thead>
                            <tr>
                                <th className="violators-expand-col" />
                                {renderSortHeader('Plate', 'plateNumber')}
                                {renderSortHeader('Violations', 'openViolations')}
                                {renderSortHeader('Status', 'status')}
                                {renderSortHeader('Date', 'latestViolationTimestamp')}
                                {showLotColumn && renderSortHeader('Lot Name', 'latestLotName')}
                                <th className="violators-actions-col" aria-label="Actions" />
                            </tr>
                        </thead>
                        <tbody>
                            {loading && items.length === 0 ? (
                                <tr>
                                    <td colSpan={showLotColumn ? 7 : 6} className="violators-loading">
                                        Loading…
                                    </td>
                                </tr>
                            ) : error ? (
                                <tr>
                                    <td colSpan={showLotColumn ? 7 : 6} className="violators-error">
                                        {error}
                                    </td>
                                </tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={showLotColumn ? 7 : 6} className="search-not-found">
                                        <div className="search-not-found-content">
                                            <span>
                                                {searchQuery ? `${searchQuery} not found` : 'No violators on file.'}
                                            </span>
                                            <button className="button-primary add-violation-btn" onClick={() => openAdd()}>
                                                <img src="/assets/violations/add_violation.svg" alt="" />
                                                Add Violation
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                items.map((row) => {
                                    const expanded = expandedPlates.has(row.plateNumber);
                                    const details = violationDetails[row.plateNumber] || [];
                                    const scrub = scrubIndex[row.plateNumber] ?? 0;
                                    return (
                                        <React.Fragment key={row.plateNumber + ':' + (row.latestViolationId || '')}>
                                            <tr
                                                className={`violator-row ${pinnedCtx?.plateNumber === row.plateNumber ? 'pinned' : ''} ${expanded ? 'expanded' : ''}`}
                                                onClick={() => handleRowClick(row)}
                                                onMouseEnter={() => {
                                                    const ctx = buildCtxFromRow(row);
                                                    lastHoveredRef.current = ctx;
                                                    setHoveredCtx(ctx);
                                                }}
                                                onMouseLeave={() => setHoveredCtx(null)}
                                            >
                                                <td className="violator-arrow-cell">
                                                    <button
                                                        type="button"
                                                        className={`expand-toggle ${expanded ? 'expanded' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleExpanded(row);
                                                        }}
                                                        aria-label={expanded ? 'Collapse' : 'Expand'}
                                                    >
                                                        <img src="/assets/violations/RowExpand.svg" alt="" />
                                                    </button>
                                                </td>
                                                <td className="violator-plate-cell">
                                                    <div className="plate-badge">{row.plateNumber}</div>
                                                </td>
                                                <td className="violator-count-cell">
                                                    <div className="violator-stack">
                                                        <span className="violator-stack-primary">
                                                            {row.openViolations} {row.openViolations === 1 ? 'Violation' : 'Violations'}
                                                        </span>
                                                        <span className="violator-stack-secondary">
                                                            {formatCurrency(row.totalAmountOwed)}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="violator-status-cell">
                                                    <span className={`status-pill status-${row.status.toLowerCase()}`}>
                                                        {row.status}
                                                    </span>
                                                </td>
                                                <td className="violator-date-cell">
                                                    {renderDateCell(row.latestViolationTimestamp)}
                                                </td>
                                                {showLotColumn && (
                                                    <td className="violator-lot-cell">
                                                        <div className="violator-lot-stack">
                                                            <span>{row.latestLotName || '—'}</span>
                                                            {row.lotViolationTotal > 0 && (
                                                                <span className="violator-lot-badge">{row.lotViolationTotal}</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                )}
                                                <td className="violator-actions-cell" onClick={(e) => e.stopPropagation()}>
                                                    <Menu
                                                        trigger={
                                                            <button type="button" className="row-action-trigger" aria-label="Actions">
                                                                <img src="/assets/MoreVertical.svg" alt="" onError={(e) => (e.currentTarget.style.display = 'none')} />
                                                                <span aria-hidden="true">⋯</span>
                                                            </button>
                                                        }
                                                        items={buildRowActions(row)}
                                                        placement="bottom-end"
                                                        matchTriggerWidth={false}
                                                        usePortal
                                                        dropdownClassName="violators-row-menu"
                                                    />
                                                </td>
                                            </tr>

                                            {expanded &&
                                                details.map((detail, idx) => {
                                                    const key = `${row.plateNumber}:${detail.violationTrackerId}`;
                                                    const isScrubbed = expanded && idx === scrub;
                                                    const isLast = idx === details.length - 1;
                                                    return (
                                                        <tr
                                                            key={detail.violationTrackerId}
                                                            ref={(el) => {
                                                                if (el) detailRowRefs.current.set(key, el);
                                                                else detailRowRefs.current.delete(key);
                                                            }}
                                                            className={`violation-detail-row ${isScrubbed ? 'scrubbed' : ''} ${isLast ? 'last-detail' : ''}`}
                                                            onMouseEnter={() => {
                                                                const ctx = buildCtxFromDetail(row, detail);
                                                                lastHoveredRef.current = ctx;
                                                                setHoveredCtx(ctx);
                                                            }}
                                                            onMouseLeave={() => setHoveredCtx(null)}
                                                            onClick={() => {
                                                                setPinnedCtx(buildCtxFromDetail(row, detail, true));
                                                                setScrub(row.plateNumber, idx);
                                                            }}
                                                        >
                                                            <td className="violation-detail-indent">
                                                                <span className="indent-elbow" />
                                                            </td>
                                                            <td className="violation-detail-type">
                                                                <span className="violation-detail-line">
                                                                    <strong className="violation-detail-index">{idx + 1}.</strong>{' '}
                                                                    {getViolationTypeDisplay(detail.violationType)}
                                                                </span>
                                                            </td>
                                                            <td className="violation-detail-amount">
                                                                {formatCurrency(detail.totalViolationAmount)}
                                                            </td>
                                                            <td>
                                                                <span className={`status-pill status-${row.status.toLowerCase()}`}>
                                                                    {row.status}
                                                                </span>
                                                            </td>
                                                            <td className="violator-date-cell">
                                                                {renderDateCell(detail.violationTimestamp)}
                                                            </td>
                                                            {showLotColumn && (
                                                                <td className="violator-lot-cell">{row.latestLotName || '—'}</td>
                                                            )}
                                                            <td className="violator-actions-cell" onClick={(e) => e.stopPropagation()}>
                                                                <Menu
                                                                    trigger={
                                                                        <button type="button" className="row-action-trigger" aria-label="Actions">
                                                                            <span aria-hidden="true">⋯</span>
                                                                        </button>
                                                                    }
                                                                    items={buildDetailActions(row, detail)}
                                                                    placement="bottom-end"
                                                                    matchTriggerWidth={false}
                                                                    usePortal
                                                                    dropdownClassName="violators-row-menu"
                                                                />
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                    <Pagination
                        currentPage={page}
                        totalPages={totalPages}
                        totalItems={totalElements}
                        itemsOnPage={items.length}
                        onPageChange={goToPage}
                        disabled={loading}
                        emptyMessage={
                            isMultiLotMode
                                ? 'Your selected lots currently have no active violations on any vehicle plate.'
                                : 'Your lot currently has no active violations on any vehicle plate.'
                        }
                    />
                </div>

                {!isMobile && (
                    <div className="violators-side" onMouseEnter={handlePanelMouseEnter}>
                    <VehicleDetailPanel
                        context={activeCtx}
                        trend={activeCtx ? trendCache[activeCtx.plateNumber] || null : null}
                        trendLoading={trendLoading && !!activeCtx && !trendCache[activeCtx.plateNumber]}
                        onClear={() => {
                            if (!activeCtx) return;
                            const row = items.find((r) => r.plateNumber === activeCtx.plateNumber);
                            if (!row) return;
                            setClearTarget({ kind: 'all', plateNumber: row.plateNumber, lotId: row.latestLotId || lotId || '' });
                        }}
                        onPrint={() => {
                            if (!activeCtx) return;
                            const row = items.find((r) => r.plateNumber === activeCtx.plateNumber);
                            if (row) void handlePrintSummary(row);
                        }}
                        onValidate={() => {
                            if (!activeCtx) return;
                            const row = items.find((r) => r.plateNumber === activeCtx.plateNumber);
                            if (row) handleValidate(row);
                        }}
                        onAddToRegistry={() => {
                            if (!activeCtx) return;
                            const row = items.find((r) => r.plateNumber === activeCtx.plateNumber);
                            if (row) void handleAddToRegistry(row);
                        }}
                        registryAdded={activeCtx ? registryAdded.has(activeCtx.plateNumber) : false}
                        onSaveNotes={handleSaveNotes}
                        scrub={(() => {
                            // Show scrub controls whenever the active plate is expanded — arrows disable themselves
                            // when there's only one violation (so the row reads "Violation 1 of 1").
                            if (!activeCtx) return null;
                            const plate = activeCtx.plateNumber;
                            const details = violationDetails[plate];
                            if (!details || details.length === 0 || !expandedPlates.has(plate)) return null;
                            const row = items.find((r) => r.plateNumber === plate);
                            if (!row) return null;
                            const idx = scrubIndex[plate] ?? 0;
                            return {
                                index: Math.min(idx, details.length - 1),
                                total: details.length,
                                onPrev: () => scrubTo(row, idx - 1),
                                onNext: () => scrubTo(row, idx + 1),
                            };
                        })()}
                    />
                    </div>
                )}
            </div>

            {/* Add Violation Modal */}
            {isAddOpen && (
                <Modal
                    isOpen={true}
                    title="Create Violation"
                    confirmText="Create Violation"
                    cancelText="Cancel"
                    onConfirm={submitAddViolation}
                    onCancel={() => setAddOpen(false)}
                    disableConfirm={
                        !addData.plateNumber.trim() ||
                        !addData.violationType ||
                        !addData.totalViolationAmount.trim() ||
                        (isMultiLotMode && !addData.selectedLotId)
                    }
                >
                    <div className="violation-form">
                        {isMultiLotMode && (
                            <Menu
                                trigger={
                                    <button
                                        type="button"
                                        className={`violation-dropdown${addData.selectedLotId ? '' : ' placeholder'}`}
                                    >
                                        <span className="violation-dropdown-label">{selectedLotLabel}</span>
                                        <img
                                            src="/assets/ArrowDropdown.svg"
                                            alt=""
                                            className={`violation-dropdown-arrow ${lotMenuOpen ? 'open' : ''}`}
                                        />
                                    </button>
                                }
                                items={lotMenuItems}
                                placement="bottom-start"
                                dropdownClassName="violation-dropdown-menu"
                                onOpenChange={setLotMenuOpen}
                                allowNoSelection
                            />
                        )}

                        <input
                            type="text"
                            className="violation-input violation-plate-input"
                            value={addData.plateNumber}
                            onChange={(e) => setAddData((p) => ({ ...p, plateNumber: e.target.value.toUpperCase() }))}
                            placeholder="PLATE"
                            maxLength={8}
                        />
                        <div className="vehicle-details-row">
                            <input
                                type="text"
                                className="violation-input"
                                value={addData.vehicleMake}
                                onChange={(e) => setAddData((p) => ({ ...p, vehicleMake: e.target.value }))}
                                placeholder="Make"
                                maxLength={16}
                            />
                            <input
                                type="text"
                                className="violation-input"
                                value={addData.vehicleModel}
                                onChange={(e) => setAddData((p) => ({ ...p, vehicleModel: e.target.value }))}
                                placeholder="Model"
                                maxLength={16}
                            />
                            <input
                                type="text"
                                className="violation-input"
                                value={addData.vehicleColor}
                                onChange={(e) => setAddData((p) => ({ ...p, vehicleColor: e.target.value }))}
                                placeholder="Colour"
                                maxLength={16}
                            />
                        </div>
                        <Menu
                            trigger={
                                <button
                                    type="button"
                                    className={`violation-dropdown${addData.violationType ? '' : ' placeholder'}`}
                                >
                                    <span className="violation-dropdown-label">{violationTypeLabel}</span>
                                    <img
                                        src="/assets/ArrowDropdown.svg"
                                        alt=""
                                        className={`violation-dropdown-arrow ${violationMenuOpen ? 'open' : ''}`}
                                    />
                                </button>
                            }
                            items={violationMenuItems}
                            placement="bottom-start"
                            dropdownClassName="violation-dropdown-menu"
                            onOpenChange={setViolationMenuOpen}
                            allowNoSelection
                        />
                        <div className="violation-input-wrapper" style={{ position: 'relative' }}>
                            <input
                                type="number"
                                className="violation-input"
                                value={addData.totalViolationAmount}
                                onChange={(e) => setAddData((p) => ({ ...p, totalViolationAmount: e.target.value }))}
                                placeholder="Cost"
                                min="0"
                                max="150"
                                step="0.01"
                                style={{ paddingLeft: 35 }}
                            />
                            <span
                                style={{
                                    position: 'absolute',
                                    left: 20,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    pointerEvents: 'none',
                                    color: 'var(--text-medium)',
                                }}
                            >
                                $
                            </span>
                        </div>
                        <textarea
                            className="violation-textarea"
                            value={addData.initialNotes}
                            onChange={(e) => setAddData((p) => ({ ...p, initialNotes: e.target.value }))}
                            placeholder="Notes"
                            rows={3}
                            maxLength={200}
                        />
                    </div>
                </Modal>
            )}

            {clearTarget && (
                <Modal
                    isOpen={true}
                    title={clearTarget.kind === 'all' ? 'Clear All Violations' : 'Clear Violation'}
                    description={
                        clearTarget.kind === 'all'
                            ? `Are you sure you'd like to remove all violations from plate ${clearTarget.plateNumber}?`
                            : `Are you sure you'd like to remove this violation from plate ${clearTarget.plateNumber}?`
                    }
                    confirmText="Clear Violation"
                    cancelText="Cancel"
                    onConfirm={confirmClear}
                    onCancel={() => setClearTarget(null)}
                />
            )}

            {printState && (
                <PrintTicketModal
                    isOpen={true}
                    onClose={() => setPrintState(null)}
                    violationDetails={printState.details}
                    lotData={lotData}
                    isSummaryTicket={printState.isSummary}
                />
            )}

            {isLiveHitOpen && (
                <Modal
                    isOpen={true}
                    title="Live HIT"
                    description="No ALPR device detected."
                    confirmText=""
                    cancelText="Close"
                    onConfirm={() => setLiveHitOpen(false)}
                    onCancel={() => setLiveHitOpen(false)}
                    disableConfirm={true}
                />
            )}
        </div>
    );
};

export default Violators;
